// We need to log warnings and errors for easier debugging wallet connection issues
/* eslint-disable no-console */
import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { CHAIN_ID, SupportedTokenName } from "@mezo/sdk"
import {
  isUnsupportedBitcoinAddressError,
  isWalletNetworkDoesNotMatchProviderChainError,
  useRefreshPassport,
  useSignOut,
} from "@mezo-org/passport"
import { useNavigate } from "@tanstack/react-router"
import {
  Connector,
  CreateConnectorFn,
  useChainId,
  useConnect,
  useDisconnect,
} from "wagmi"

import {
  disconnectWalletAccount,
  fetchAllTransactions,
  fetchTokenAllowances,
  fetchWalletBalances,
  selectAccountCreationStatus,
  selectActiveToken,
  selectDepositFromAnotherWallet,
  selectMezoWalletBalances,
  selectPortalStatistics,
  setAccountCreationStatus,
  setDepositFromAnotherWallet,
  setHasLoadedWalletBalances,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"
import {
  ETHEREUM_NETWORK,
  routes,
  WAGMI_CONNECTOR_ID,
  WALLET_ANALYTICS_ID,
} from "#/shared/constants"
import { FlowContext } from "#/shared/context"
import { AccountCreationStatus, ExtendedConector, Flow } from "#/shared/types"
import { bigIntToFloat } from "#/shared/utils"

import { useMezoAssetsBalance } from "./assets"
import { useResetStateOnDisconnect } from "./helpers"
import { useWalletConnectionNotification } from "./notification"
import { useResetPosthogUser } from "./posthog"
import { usePosthogWalletConnectedEvent } from "./posthog-events"
import { useTokenUsdPrice } from "./token"

type FetchBalancesProps = {
  fetchOnChainData?: boolean
  refetch?: boolean
}

export const useWalletDisconnect = (): {
  disconnect: (shouldRedirect?: boolean, redirectRoute?: string) => void
} => {
  const { disconnect } = useDisconnect()
  const resetPosthogUser = useResetPosthogUser()
  const { signOutAsync } = useSignOut()

  const dispatch = useDappDispatch()
  const navigate = useNavigate()

  const disconnectCallback = useCallback(
    async (
      shouldRedirect: boolean = false,
      redirectRoute: string = routes.OVERVIEW,
    ) => {
      disconnect()
      dispatch(disconnectWalletAccount())
      resetPosthogUser()

      // To get rid of a current session we need to sign out user
      await signOutAsync()

      if (shouldRedirect) {
        navigate({ to: redirectRoute })
      }
    },
    [disconnect, dispatch, resetPosthogUser, navigate, signOutAsync],
  )

  return { disconnect: disconnectCallback }
}

export const useAnotherWalletToggle = () => {
  const activeToken = useDappSelector(selectActiveToken)
  const [isToggleDisabled, setIsToggleDisabled] = useState(false)

  const depositFromAnotherWallet = useDappSelector(
    selectDepositFromAnotherWallet,
  )

  const { isEthereumFlow } = useContext(FlowContext)
  const dispatch = useDappDispatch()

  useEffect(() => {
    const btcDepositOnEthereumFlow = isEthereumFlow && activeToken === "BTC"
    const toggleDisabled = activeToken !== "BTC"

    setIsToggleDisabled(toggleDisabled)
    dispatch(setDepositFromAnotherWallet(btcDepositOnEthereumFlow))
  }, [isEthereumFlow, activeToken, dispatch])

  const toggleHandler = useCallback(() => {
    if (isToggleDisabled || (isEthereumFlow && activeToken === "BTC")) return
    dispatch(setDepositFromAnotherWallet(!depositFromAnotherWallet))
  }, [
    dispatch,
    depositFromAnotherWallet,
    isEthereumFlow,
    activeToken,
    isToggleDisabled,
  ])

  return { isToggleDisabled, toggleHandler }
}

export const useConnectWalletModal = (onClose: () => void) => {
  const dispatch = useDappDispatch()

  const chainId = useChainId()
  const { connect } = useConnect()
  const accountCreationStatus = useDappSelector(selectAccountCreationStatus)

  const [connectorToReconnect, setConnectorToReconnect] =
    useState<ExtendedConector | null>(null)

  const [hasConnectionError, setHasConnectionError] = useState(false)

  const [isUnsupportedAddressType, setIsUnsupportedAddressType] =
    useState(false)

  const [pendingConnectionId, setPendingConnectionId] = useState<
    string | null
  >()

  const [isOnWrongNetwork, setIsOnWrongNetwork] = useState(false)

  /**
   * Some wallets (like WalletConnect for example) can use external modals
   * provided by the external lib related to that wallet. In such cases we have
   * to make sure our actual modal won't get in a way - we want to have it in
   * the background.
   */
  const [isBackgroundModal, setIsBackgroundModal] = useState(false)

  const { showConnectionNotification } = useWalletConnectionNotification()
  const { emitWalletConnectedErrorEvent } = usePosthogWalletConnectedEvent()

  useEffect(() => {
    if (accountCreationStatus === AccountCreationStatus.Idle) {
      setPendingConnectionId(null)
    }
  }, [accountCreationStatus])

  const resetConnectionState = useCallback(() => {
    setHasConnectionError(false)
    setConnectorToReconnect(null)
    setIsUnsupportedAddressType(false)
    setIsOnWrongNetwork(false)
  }, [])

  const onSuccess = useCallback(() => {
    showConnectionNotification("success")
  }, [showConnectionNotification])

  const onError = useCallback(
    (
      error: Error & { cause?: Error },
      variables: { connector: Connector | CreateConnectorFn },
    ) => {
      // FIXME: Investigate why console.error fails on the execution
      console.log(error)

      if (error.cause) {
        if (isWalletNetworkDoesNotMatchProviderChainError(error.cause)) {
          setIsOnWrongNetwork(true)
        } else if (isUnsupportedBitcoinAddressError(error.cause)) {
          setIsUnsupportedAddressType(true)
        }
        setIsBackgroundModal(false)
        setConnectorToReconnect(variables.connector as ExtendedConector)
        setHasConnectionError(true)
      }

      showConnectionNotification("fail")
      emitWalletConnectedErrorEvent()
    },
    [showConnectionNotification, emitWalletConnectedErrorEvent],
  )

  const onConnect = useCallback(
    (connector: Connector) => {
      setPendingConnectionId(connector.id)
      dispatch(setAccountCreationStatus(AccountCreationStatus.Connecting))
      resetConnectionState()
      if (connector.id === WAGMI_CONNECTOR_ID.walletConnect) {
        setIsBackgroundModal(true)
      }
      connect(
        {
          connector,
          chainId:
            connector.type === "orangekit"
              ? CHAIN_ID[ETHEREUM_NETWORK]
              : chainId,
        },
        { onSuccess, onError },
      )
    },
    [dispatch, resetConnectionState, connect, chainId, onSuccess, onError],
  )

  const onCloseModal = useCallback(() => {
    resetConnectionState()
    setPendingConnectionId(null)
    setIsBackgroundModal(false)
    onClose()
  }, [resetConnectionState, onClose])

  return {
    connectorToReconnect,
    hasConnectionError,
    isUnsupportedAddressType,
    isOnWrongNetwork,
    pendingConnectionId,
    isBackgroundModal,
    onConnect,
    onCloseModal,
  }
}

export const useWalletConnectors = () => {
  const { connectors: connectorsFromUseConnect, isError: isMaybeRejected } =
    useConnect()

  const connectors = connectorsFromUseConnect.map((connector) => {
    if (
      connector.id === WAGMI_CONNECTOR_ID.walletConnect &&
      "rkDetails" in (connector as ExtendedConector)
    ) {
      const extendedConnector = connector as ExtendedConector

      return {
        ...extendedConnector,
        id: extendedConnector.rkDetails?.id,
        icon: extendedConnector.rkDetails?.iconUrl,
        name: extendedConnector.rkDetails?.name,
        analyticsId:
          WALLET_ANALYTICS_ID[
            extendedConnector.rkDetails?.id as keyof typeof WALLET_ANALYTICS_ID
          ],
      }
    }

    if (connector.id === WAGMI_CONNECTOR_ID.okx) {
      return {
        ...connector,
        name: "OKX",
        analyticsId: WALLET_ANALYTICS_ID["orangekit-okx"],
      }
    }

    return {
      ...connector,
      analyticsId:
        WALLET_ANALYTICS_ID[connector.id as keyof typeof WALLET_ANALYTICS_ID],
    }
  })

  return { connectors, isMaybeRejected }
}

export const useWalletTypeFlow = () => {
  const { isBitcoinFlow, setFlow } = useContext(FlowContext)

  const switchFlowHandler = useCallback(() => {
    setFlow(isBitcoinFlow ? Flow.ETHEREUM : Flow.BITCOIN)
  }, [isBitcoinFlow, setFlow])

  return { switchFlow: switchFlowHandler }
}

export const useTotalMezoWalletUSDValue = () => {
  const walletBalances = useDappSelector(selectMezoWalletBalances)
  const statistics = useDappSelector(selectPortalStatistics)
  const { getTokenUsdPrice } = useTokenUsdPrice()

  const walletBalancesSum = useMemo(() => {
    if (!statistics) return 0

    return Object.entries(walletBalances).reduce(
      (sum, [token, { balance, decimals }]) => {
        const floatAmount = bigIntToFloat(
          balance,
          Number(decimals),
          Number(decimals),
        )

        return (
          sum +
          Number(floatAmount) * getTokenUsdPrice(token as SupportedTokenName)
        )
      },
      0,
    )
  }, [walletBalances, statistics, getTokenUsdPrice])

  return { totalWalletUSDValue: walletBalancesSum }
}

export const useFetchWalletBalances = () => {
  const [balanceFetched, setBalancesFetched] = useState(false)

  const { fetchMezoAssetsBalance } = useMezoAssetsBalance()
  const { refreshTokensBalances } = useRefreshPassport()

  const dispatch = useDappDispatch()

  useResetStateOnDisconnect<boolean>(setBalancesFetched, false)

  const fetchWalletBalancesHandler = useCallback(
    async ({
      fetchOnChainData = false,
      refetch = false,
    }: FetchBalancesProps) => {
      if (balanceFetched && !refetch) return

      await dispatch(fetchAllTransactions())

      if (fetchOnChainData) {
        await dispatch(fetchWalletBalances())
        await dispatch(fetchTokenAllowances())
      }

      await fetchMezoAssetsBalance()
      await refreshTokensBalances()

      setBalancesFetched(true)
      dispatch(setHasLoadedWalletBalances(true))
    },
    [dispatch, fetchMezoAssetsBalance, refreshTokensBalances, balanceFetched],
  )

  return { fetchWalletBalances: fetchWalletBalancesHandler }
}
